import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LOGO = 'https://static.readdy.ai/image/1e1c36a8fe2eee6efab369c345d12000/d2de7d124fc44cc7dd03173e7e2df5a4.png';

const pillarIcons = ['ri-plant-line', 'ri-home-heart-line', 'ri-brain-line', 'ri-compass-3-line', 'ri-shield-star-line', 'ri-git-network-line'];
const pillarNames = ['Horizonte Arraigado', 'Bio-Santuario', 'Alquimia Mental', 'Astrolabio Interior', 'Maestría Multidimensional', 'Plano Sistémico'];

export default function DashboardPage() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const displayName = profile?.name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Nodo';

  return (
    <div className="min-h-screen bg-[#1A1C20]" style={{
      backgroundImage: 'linear-gradient(rgba(139,157,131,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(139,157,131,0.03) 1px, transparent 1px)',
      backgroundSize: '40px 40px',
    }}>
      {/* Top Bar */}
      <header className="border-b border-[#F8F6F3]/8 bg-[#1A1C20]/95 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-screen-xl mx-auto px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Nube Geodésica" className="h-8 w-auto object-contain" />
            <div className="w-px h-5 bg-[#F8F6F3]/10" />
            <span className="text-[#8B9D83] text-[10px] uppercase tracking-[3px]">Centro de Control</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#8B9D83] animate-pulse" />
              <span className="text-[#F8F6F3]/60 text-xs">Nodo activo</span>
            </div>
            <div className="w-px h-4 bg-[#F8F6F3]/10" />
            <span className="text-[#F8F6F3]/60 text-sm">{displayName}</span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-[#F8F6F3]/40 hover:text-[#C97B63] text-xs uppercase tracking-[1.5px] transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line text-sm" /> Desconectar
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-8 py-12">
        {/* Welcome */}
        <div className="mb-12">
          <p className="text-[#8B9D83] text-[10px] uppercase tracking-[4px] mb-3">— Bienvenido a tu espacio —</p>
          <h1 className="text-[#F8F6F3] font-bold mb-2" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.5px' }}>
            Hola, {displayName} ⊹
          </h1>
          <p className="text-[#F8F6F3]/40 text-base max-w-xl">
            Tu trazado ha comenzado. Explora el Módulo 0 para iniciar tu proceso de Re-ingeniería Espiritual.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-12">
          {[
            { label: 'Plan activo', value: profile?.plan === 'free' ? 'Gratuito' : profile?.plan || 'Gratuito', icon: 'ri-vip-crown-line', color: '#8B9D83' },
            { label: 'Módulos completados', value: `${(profile?.modules_unlocked?.length || 1) - 1} / 6`, icon: 'ri-checkbox-circle-line', color: '#C97B63' },
            { label: 'Estado del nodo', value: 'Activo', icon: 'ri-wifi-line', color: '#8B9D83' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl p-6" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.08)' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${stat.color}15` }}>
                  <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                </div>
                <span className="text-[#F8F6F3]/40 text-[10px] uppercase tracking-[2px]">{stat.label}</span>
              </div>
              <p className="text-[#F8F6F3] font-bold text-xl">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Module 0 - Unlocked */}
        <div className="mb-12">
          <h2 className="text-[#F8F6F3] font-semibold text-lg mb-6 flex items-center gap-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-2-line text-[#8B9D83] text-base" />
            </div>
            Tu Recorrido Modular
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Module 0 Card - Unlocked */}
            <div className="lg:col-span-2 rounded-2xl overflow-hidden relative" style={{ border: '1px solid rgba(139,157,131,0.3)', background: 'linear-gradient(135deg, rgba(139,157,131,0.08) 0%, rgba(26,28,32,0.95) 100%)' }}>
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-[3px] px-3 py-1 rounded-full" style={{ background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.3)' }}>
                        Módulo 0 · Desbloqueado
                      </span>
                    </div>
                    <h3 className="text-[#F8F6F3] font-bold text-2xl mb-2" style={{ letterSpacing: '-0.3px' }}>El Origen</h3>
                    <p className="text-[#F8F6F3]/50 text-sm leading-relaxed max-w-md">
                      Bienvenido al umbral. En este módulo introductorio comenzamos la Cartografía del Alma: un diagnóstico profundo del estado actual de tu arquitectura interna.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: 'ri-play-circle-line', label: 'Video introductorio', desc: '28 min' },
                    { icon: 'ri-file-pdf-line', label: 'Manual del Módulo 0', desc: 'PDF descargable' },
                    { icon: 'ri-questionnaire-line', label: 'Evaluación inicial', desc: '10 preguntas' },
                    { icon: 'ri-map-pin-line', label: 'Mapa de tu trazado', desc: 'Infografía' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 rounded-xl p-4 cursor-pointer hover:border-[#8B9D83]/50 transition-all duration-200" style={{ background: 'rgba(248,246,243,0.04)', border: '1px solid rgba(248,246,243,0.06)' }}>
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(139,157,131,0.12)' }}>
                        <i className={`${item.icon} text-sm text-[#8B9D83]`} />
                      </div>
                      <div>
                        <p className="text-[#F8F6F3]/80 text-xs font-medium">{item.label}</p>
                        <p className="text-[#F8F6F3]/30 text-[10px]">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => navigate('/nodo-0')}
                  className="flex items-center gap-3 bg-[#8B9D83] text-[#1A1C20] font-bold text-sm px-8 py-4 rounded-xl hover:bg-[#7c8f74] transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-base" />
                  Comenzar el Módulo 0
                </button>
              </div>
            </div>

            {/* Upgrade Card */}
            <div className="rounded-2xl p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(201,123,99,0.12) 0%, rgba(26,28,32,0.95) 100%)', border: '1px solid rgba(201,123,99,0.25)' }}>
              <p className="text-[#C97B63] text-[10px] uppercase tracking-[3px] mb-4">— Acceso Completo —</p>
              <h3 className="text-[#F8F6F3] font-bold text-xl mb-3" style={{ letterSpacing: '-0.3px' }}>Activa el Blueprint Completo</h3>
              <p className="text-[#F8F6F3]/40 text-sm mb-6 leading-relaxed">Los 6 pilares del sistema. Progresión secuencial. Transformación sistémica.</p>

              <div className="space-y-3 mb-8">
                {['6 Módulos completos', 'Quizzes automáticos', 'Infografías descargables', 'App Estelarte Pro', 'Comunidad privada'].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-check-line text-[#C97B63] text-xs" />
                    </div>
                    <span className="text-[#F8F6F3]/60 text-xs">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <p className="text-[#F8F6F3]/30 text-xs mb-1">Desde</p>
                <p className="text-[#F8F6F3] font-bold text-2xl">€100<span className="text-[#F8F6F3]/40 text-sm font-normal">/mes</span></p>
              </div>

              <button className="w-full bg-[#C97B63] text-[#F8F6F3] font-semibold text-sm py-3.5 rounded-xl hover:bg-[#b86d55] transition-all duration-300 cursor-pointer whitespace-nowrap">
                Ver Planes de Acceso
              </button>
            </div>
          </div>
        </div>

        {/* Locked Modules */}
        <div>
          <h2 className="text-[#F8F6F3]/40 font-semibold text-sm mb-4 flex items-center gap-2 uppercase tracking-[2px]">
            <i className="ri-lock-2-line text-xs" />
            Módulos bloqueados
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {pillarNames.map((name, i) => (
              <div
                key={name}
                className="rounded-xl p-5 flex items-center gap-4 opacity-40"
                style={{ background: 'rgba(248,246,243,0.03)', border: '1px solid rgba(248,246,243,0.06)' }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: 'rgba(248,246,243,0.06)' }}>
                  <i className={`${pillarIcons[i]} text-[#F8F6F3]/40 text-base`} />
                </div>
                <div>
                  <p className="text-[#F8F6F3]/60 text-xs font-medium">Módulo {i + 1}</p>
                  <p className="text-[#F8F6F3]/40 text-xs">{name}</p>
                </div>
                <div className="ml-auto w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-lock-2-line text-[#F8F6F3]/20 text-sm" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
